import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, ShieldCheck, ShieldAlert, RefreshCcw, AlertTriangle, BadgeCheck, Inbox, RotateCcw, Loader2,
  Users, TrendingUp, ClipboardCheck, Calendar,
} from 'lucide-react';
import { toast } from 'sonner';

const StatusChip = ({ row }) => {
  if (row.ggf_certified) return (
    <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase bg-emerald-100 text-emerald-800 border-emerald-300" data-testid="chip-certified">
      <ShieldCheck className="w-3 h-3" /> Certified
    </span>
  );
  if (row.ggf_cert_override) return (
    <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase bg-amber-100 text-amber-900 border-amber-300" data-testid="chip-override">
      <BadgeCheck className="w-3 h-3" /> Override
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase bg-rose-100 text-rose-800 border-rose-300" data-testid="chip-uncertified">
      <ShieldAlert className="w-3 h-3" /> Not Certified
    </span>
  );
};

const GgfAudit = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [overrideLog, setOverrideLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [replacingId, setReplacingId] = useState(null);
  // Iter-246b — GGF Coverage widget.
  const [coverage, setCoverage] = useState(null);
  // Sprint-GGF-E2E — pipeline health + admin ops.
  const [pipeline, setPipeline] = useState(null);
  const [runningBackfill, setRunningBackfill] = useState(false);
  const [runningReminders, setRunningReminders] = useState(false);

  const runBackfill = async () => {
    setRunningBackfill(true);
    try {
      const r = await api.post('/admin/ggf/backfill/run');
      toast.success(`Backfill: scanned ${r.data.attempted}, notified ${r.data.notified}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed');
    } finally {
      setRunningBackfill(false);
    }
  };

  const runDryRun = async () => {
    try {
      const r = await api.post('/admin/ggf/backfill/dry-run');
      const list = r.data.would_notify || [];
      const names = list.slice(0, 5).map(x => x.talent_name || x.talent_email).join(', ');
      toast.message(
        `Preview: ${list.length} talent would be notified`,
        { description: list.length ? names + (list.length > 5 ? ` +${list.length-5} more` : '') : 'All good — none pending.' },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Dry-run failed');
    }
  };

  const runReminders = async () => {
    setRunningReminders(true);
    try {
      const r = await api.post('/admin/ggf/reminders/run');
      toast.success(`Reminders: attempted ${r.data.attempted}, sent ${r.data.notified}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reminders failed');
    } finally {
      setRunningReminders(false);
    }
  };

  const replaceWithCertified = async (row) => {
    if (!row.booking_id || !row.shift_id) return;
    if (!window.confirm(
      `Replace ${row.talent_name || row.talent_email} with the top GGF-certified applicant?\n\n` +
      `Old booking will be cancelled. Only certified applicants are eligible (auto-filtered).`
    )) return;
    setReplacingId(row.booking_id);
    try {
      const res = await api.post(
        `/admin/shifts/${row.shift_id}/replace-with-top`,
        { booking_id_to_replace: row.booking_id, reason: 'ggf_audit_replace' },
      );
      toast.success(`Replaced with ${res.data?.talent_name || 'top applicant'}`);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && /no pending/i.test(detail)) {
        toast.error(
          'No certified applicants are pending on this shift. Invite GGF-certified talent or use the manual override path.',
          { duration: 6000 },
        );
      } else {
        toast.error(detail || 'Replacement failed');
      }
    } finally {
      setReplacingId(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [a, b, c, p] = await Promise.all([
        api.get('/admin/ggf/audit'),
        api.get('/admin/ggf/override-log?limit=50'),
        api.get('/admin/ggf/coverage'),
        api.get('/admin/ggf/pipeline-status'),
      ]);
      setData(a.data);
      setOverrideLog(b.data?.entries || []);
      setCoverage(c.data || null);
      setPipeline(p.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load GGF audit');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return <div className="p-6 text-sm text-gray-500">Loading…</div>;
  }
  const rows = showAll ? data.rows : data.rows.filter((r) => r.needs_action || r.ggf_cert_override);

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="admin-ggf-audit">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-emerald-600" /> Guardian Garage Floors — Certification Audit
        </h1>
        <Button
          variant="outline"
          size="sm"
          className="ml-auto"
          onClick={() => navigate('/admin/ggf/settings')}
          data-testid="ggf-open-settings-btn"
        >
          Settings
        </Button>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {/* Sprint-GGF-E2E (Feb 2026) — Notification Pipeline Health. */}
      {pipeline?.summary && (
        <Card className="mb-5 border-cyan-200" data-testid="ggf-pipeline-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-cyan-600" />
              Notification Pipeline Health
              <span className="text-xs text-gray-500 font-normal">
                (future-dated GGF bookings)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 text-center mb-3">
              {[
                ['Total', pipeline.summary.total_future_bookings, 'text-slate-900'],
                ['Certified', pipeline.summary.certified, 'text-emerald-700'],
                ['Pending', pipeline.summary.certification_pending, 'text-amber-700'],
                ['Portal Visible', pipeline.summary.portal_visible, 'text-cyan-700'],
                ['Email Sent', pipeline.summary.email_sent, 'text-sky-700'],
                ['SMS Sent', pipeline.summary.sms_sent, 'text-indigo-700'],
                ['<24h Imminent', pipeline.summary.imminent_lt_24h, 'text-orange-700'],
                ['Pipe Failures', pipeline.summary.pipeline_failure, pipeline.summary.pipeline_failure > 0 ? 'text-rose-700 font-extrabold' : 'text-emerald-700'],
              ].map(([label, v, cls]) => (
                <div key={label} className="border rounded-lg py-2">
                  <div className={`text-lg font-bold ${cls}`} data-testid={`pipe-${label.toLowerCase().replace(/\W+/g,'-')}`}>{v}</div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={runDryRun} data-testid="ggf-dry-run-btn">
                Dry-run backfill (preview only)
              </Button>
              <Button size="sm" onClick={runBackfill} disabled={runningBackfill} data-testid="ggf-backfill-btn">
                {runningBackfill && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                Run backfill now
              </Button>
              <Button size="sm" variant="outline" onClick={runReminders} disabled={runningReminders} data-testid="ggf-reminders-btn">
                {runningReminders && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                Fire reminder pass
              </Button>
            </div>
            {pipeline.summary.pipeline_failure > 0 && (
              <div className="mt-3 rounded border-l-4 border-rose-500 bg-rose-50 text-rose-900 text-xs px-3 py-2" data-testid="pipe-failure-banner">
                <AlertTriangle className="inline w-3.5 h-3.5 mr-1" />
                <strong>{pipeline.summary.pipeline_failure}</strong> bookings have a broken notification pipeline. These talent will NOT be penalised for missing the assessment — treat as a platform failure and investigate below.
              </div>
            )}
            {/* Per-booking pipeline detail — only show if there are pending bookings. */}
            {pipeline.bookings?.length > 0 && (
              <details className="mt-3">
                <summary className="text-xs text-cyan-800 cursor-pointer hover:underline">
                  Show per-booking pipeline detail ({pipeline.bookings.length} rows)
                </summary>
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-xs" data-testid="pipe-detail-table">
                    <thead className="bg-cyan-50 text-cyan-900">
                      <tr>
                        <th className="text-left px-2 py-1">Shift Date</th>
                        <th className="text-left px-2 py-1">Talent</th>
                        <th className="text-left px-2 py-1">Certified?</th>
                        <th className="text-left px-2 py-1">Portal</th>
                        <th className="text-left px-2 py-1">Email</th>
                        <th className="text-left px-2 py-1">SMS</th>
                        <th className="text-left px-2 py-1">72h</th>
                        <th className="text-left px-2 py-1">24h</th>
                        <th className="text-left px-2 py-1">4h</th>
                        <th className="text-left px-2 py-1">Pipeline</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {pipeline.bookings.map((b) => (
                        <tr key={b.booking_id} className={b.pipeline_failure ? 'bg-rose-50' : ''}>
                          <td className="px-2 py-1 font-mono">{b.shift_date}</td>
                          <td className="px-2 py-1">
                            <div>{b.talent_name || '—'}</div>
                            <div className="text-[10px] text-gray-500">{b.talent_email}</div>
                          </td>
                          <td className="px-2 py-1">{b.ggf_certified ? '✅' : '❌'}</td>
                          <td className="px-2 py-1">{b.portal_visible ? '✅' : '—'}</td>
                          <td className="px-2 py-1">{b.email_sent ? '✅' : '—'}</td>
                          <td className="px-2 py-1">{b.sms_sent ? '✅' : '—'}</td>
                          <td className="px-2 py-1">{b.reminder_72h_sent ? '✅' : '—'}</td>
                          <td className="px-2 py-1">{b.reminder_24h_sent ? '✅' : '—'}</td>
                          <td className="px-2 py-1">{b.reminder_4h_sent ? '✅' : '—'}</td>
                          <td className="px-2 py-1">
                            {b.pipeline_failure ? (
                              <span className="text-rose-700 font-bold">FAIL</span>
                            ) : b.ggf_certified ? (
                              <span className="text-emerald-700">✓</span>
                            ) : (
                              <span className="text-cyan-700">Open</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </CardContent>
        </Card>
      )}

      {/* Iter-246b — GGF Coverage Widget (Mel-requested headline tile row). */}
      {coverage && (
        <div
          className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5"
          data-testid="ggf-coverage-widget"
        >
          <div className="bg-white border border-emerald-200 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">
                Total Certified
              </div>
            </div>
            <div className="text-2xl font-extrabold text-slate-900" data-testid="coverage-total-certified">
              {coverage.total_certified_talent}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">talent on roster</div>
          </div>

          <div className="bg-white border border-cyan-200 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-cyan-600" />
              <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-700">
                Active Coverage
              </div>
            </div>
            <div className="text-2xl font-extrabold text-slate-900" data-testid="coverage-active-pct">
              {coverage.active_coverage_pct}%
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {coverage.ggf_active_certified} / {coverage.ggf_active_talent_total} GGF-booked talent
            </div>
          </div>

          <div className="bg-white border border-violet-200 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <ClipboardCheck className="w-4 h-4 text-violet-600" />
              <div className="text-[10px] uppercase tracking-wider font-bold text-violet-700">
                Applicants MTD
              </div>
            </div>
            <div className="text-2xl font-extrabold text-slate-900" data-testid="coverage-applicants-mtd">
              {coverage.certified_applicants_mtd}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">certified applicants this month</div>
          </div>

          <div className="bg-white border border-amber-200 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-4 h-4 text-amber-600" />
              <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">
                Bookings MTD
              </div>
            </div>
            <div className="text-2xl font-extrabold text-slate-900" data-testid="coverage-bookings-mtd">
              {coverage.certified_bookings_mtd}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">certified bookings this month</div>
          </div>
        </div>
      )}

      {/* Summary stripe */}
      <Card className="mb-5">
        <CardContent className="p-4 flex flex-wrap gap-3 items-center text-sm">
          <Badge className="bg-slate-100 text-slate-800 border-slate-300" data-testid="audit-total">
            {data.total_bookings} GGF bookings
          </Badge>
          <Badge className="bg-rose-100 text-rose-800 border-rose-300" data-testid="audit-uncertified">
            {data.uncertified_active} uncertified active — needs replacement or override
          </Badge>
          <Button
            size="sm" variant={showAll ? 'default' : 'outline'}
            className="ml-auto" onClick={() => setShowAll(!showAll)}
            data-testid="audit-toggle-show-all"
          >
            {showAll ? 'Hide certified' : 'Show all bookings'}
          </Button>
        </CardContent>
      </Card>

      {/* Audit table */}
      <Card className="mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Bookings</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="p-6 text-sm text-gray-500" data-testid="audit-empty">
              {data.uncertified_active === 0 ? 'All GGF bookings are certified ✅' : 'No rows to display.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="audit-table">
                <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                  <tr>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-left px-4 py-2">Date</th>
                    <th className="text-left px-4 py-2">Venue</th>
                    <th className="text-left px-4 py-2">Talent</th>
                    <th className="text-left px-4 py-2">Booking</th>
                    <th className="text-left px-4 py-2">Source</th>
                    <th className="text-left px-4 py-2">Note</th>
                    <th className="text-left px-4 py-2">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((r) => (
                    <tr key={r.booking_id} data-testid={`audit-row-${r.booking_id}`}>
                      <td className="px-4 py-2"><StatusChip row={r} /></td>
                      <td className="px-4 py-2 font-mono text-xs">{r.shift_date}</td>
                      <td className="px-4 py-2">{r.venue_name || <span className="text-gray-400">—</span>}</td>
                      <td className="px-4 py-2">
                        <div className="font-medium">{r.talent_name || '—'}</div>
                        <div className="text-xs text-gray-500">{r.talent_email}</div>
                      </td>
                      <td className="px-4 py-2 text-xs">
                        <span className="font-mono text-gray-600">{r.booking_status}</span>
                        <div className="text-[10px] text-gray-400">{r.assignment_type}</div>
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-600">
                        {r.ggf_certified
                          ? (r.ggf_certified_source === 'admin_manual' ? 'Manual' : 'Self-test')
                          : (r.ggf_cert_override ? 'Override' : '—')}
                        {r.ggf_test_score != null && (
                          <span className="ml-1 text-emerald-700 font-mono">{r.ggf_test_score}%</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-700 max-w-[260px]">
                        {r.ggf_cert_override_reason ? (
                          <span title={r.ggf_cert_override_reason}>
                            <AlertTriangle className="inline w-3 h-3 text-amber-500 mr-1" />
                            {r.ggf_cert_override_reason}
                          </span>
                        ) : r.needs_action ? (
                          <span className="text-rose-700 font-semibold">Replace or override required</span>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-2">
                        {r.needs_action ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs"
                            disabled={replacingId === r.booking_id}
                            onClick={() => replaceWithCertified(r)}
                            data-testid={`replace-btn-${r.booking_id}`}
                          >
                            {replacingId === r.booking_id ? (
                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            ) : (
                              <RotateCcw className="w-3 h-3 mr-1" />
                            )}
                            Replace with top certified
                          </Button>
                        ) : (
                          <span className="text-[11px] text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Override log */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Inbox className="w-4 h-4 text-amber-500" /> Override Audit Log
            <span className="text-xs text-gray-500 font-normal">
              ({overrideLog.length} entries)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {overrideLog.length === 0 ? (
            <div className="p-4 text-sm text-gray-500">No overrides recorded.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                  <tr>
                    <th className="text-left px-4 py-2">When</th>
                    <th className="text-left px-4 py-2">Talent</th>
                    <th className="text-left px-4 py-2">Shift</th>
                    <th className="text-left px-4 py-2">Override By</th>
                    <th className="text-left px-4 py-2">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {overrideLog.map((e) => (
                    <tr key={e.id}>
                      <td className="px-4 py-2 text-xs text-gray-600">{e.created_at && new Date(e.created_at).toLocaleString()}</td>
                      <td className="px-4 py-2">
                        <div className="font-medium">{e.talent_name}</div>
                        <div className="text-xs text-gray-500">{e.talent_email}</div>
                      </td>
                      <td className="px-4 py-2 text-xs">
                        <div>{e.venue_name}</div>
                        <div className="text-gray-500">{e.shift_date}</div>
                      </td>
                      <td className="px-4 py-2 text-xs">{e.override_by_email}</td>
                      <td className="px-4 py-2 text-xs">{e.override_reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GgfAudit;
